<script lang="ts">
  import { getBudgetBadgeVariant, type BudgetType } from '../../lib/budgetUtils';

  type Props = {
    percentage: number;
    spent: number;
    limit: number;
    showLabel?: boolean;
    rolloverAmount?: number;
    totalLimit?: number;
    isFixedExpense?: boolean;
    budgetType?: BudgetType;
    periodDate?: Date;
  };

  let { percentage, spent, limit, showLabel = true, rolloverAmount = 0, totalLimit, isFixedExpense = false, budgetType = 'monthly', periodDate = new Date() }: Props = $props();

  const effectiveTotalLimit = $derived(totalLimit || limit);
  const hasRollover = $derived(rolloverAmount > 0);

  // Calculate percentages for segmented display
  const baseLimitPercentage = $derived((limit / effectiveTotalLimit) * 100);
  const rolloverPercentage = $derived((rolloverAmount / effectiveTotalLimit) * 100);
  const spentOfBase = $derived(Math.min(spent, limit));
  const spentOfRollover = $derived(Math.max(0, spent - limit));
  const spentBasePercentage = $derived((spentOfBase / effectiveTotalLimit) * 100);
  const spentRolloverPercentage = $derived((spentOfRollover / effectiveTotalLimit) * 100);

  // Check if we're over the total limit (including rollover)
  const isOverBudget = $derived(spent > effectiveTotalLimit);

  // Check if we're using rollover funds (spent more than base limit)
  const isUsingRollover = $derived(spent > limit);

  // Use the same badge logic for consistent colors
  const badgeVariant = $derived(getBudgetBadgeVariant(percentage, budgetType, periodDate, isFixedExpense));

  // Map badge variant to colors
  const getColorFromVariant = (variant: 'danger' | 'warning' | 'success'): string => {
    if (variant === 'danger') return 'var(--color-danger)';
    if (variant === 'warning') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getBackgroundColorFromVariant = (variant: 'danger' | 'warning' | 'success'): string => {
    if (variant === 'danger') return 'var(--color-danger-light)';
    if (variant === 'warning') return 'var(--color-warning-light)';
    return 'var(--color-success-light)';
  };

  const color = $derived(getColorFromVariant(badgeVariant));
  const backgroundColor = $derived(getBackgroundColorFromVariant(badgeVariant));
  const displayPercentage = $derived(Math.min(percentage, 100));
</script>

<div class="progress-container">
  {#if showLabel}
    <div class="progress-label" style:color={color}>
      <span class="spent">${spent.toFixed(2)}</span>
      <span class="separator">/</span>
      <span class="limit">${limit.toFixed(2)}</span>
      <span class="percentage">({percentage.toFixed(1)}%)</span>
    </div>
  {/if}

  {#if hasRollover}
    <!-- Segmented progress bar showing base + rollover -->
    <div class="progress-bar-segmented">
      <!-- Base budget segment -->
      <div class="segment base-segment" style:width="{baseLimitPercentage}%">
        <div class="segment-fill" style:width="{(spentOfBase / limit) * 100}%" style:background-color={color}></div>
      </div>
      <!-- Rollover segment -->
      <div class="segment rollover-segment" class:over-budget={isOverBudget} class:using-rollover={isUsingRollover && !isOverBudget} style:width="{rolloverPercentage}%">
        <div class="segment-fill" style:width="{(spentOfRollover / rolloverAmount) * 100}%" style:background-color={color}></div>
      </div>
    </div>
  {:else}
    <!-- Regular progress bar -->
    <div class="progress-bar" style:background-color={backgroundColor}>
      <div
        class="progress-fill"
        style:width="{displayPercentage}%"
        style:background-color={color}
      ></div>
    </div>
  {/if}
</div>

<style>
  .progress-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
  }

  .progress-bar {
    width: 100%;
    height: var(--progress-bar-height);
    border-radius: var(--radius-full);
    overflow: hidden;
    position: relative;
  }

  .progress-fill {
    height: 100%;
    border-radius: var(--radius-full);
    transition: width var(--transition-base), background-color var(--transition-base);
  }

  .progress-label {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  .spent {
    font-weight: var(--font-weight-semibold);
  }

  .separator {
    color: var(--color-text-tertiary);
  }

  .limit {
    color: var(--color-text-secondary);
  }

  .percentage {
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
    margin-left: var(--spacing-xs);
  }

  .rollover-label {
    font-size: var(--font-size-xs);
    color: var(--color-text-tertiary);
    margin-left: var(--spacing-xs);
  }

  .progress-bar-segmented {
    width: 100%;
    height: var(--progress-bar-height);
    display: flex;
    gap: 2px;
    border-radius: var(--radius-full);
    overflow: hidden;
  }

  .segment {
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .base-segment {
    background: var(--color-background-secondary);
    border-radius: var(--radius-full) 0 0 var(--radius-full);
  }

  .rollover-segment {
    background: var(--color-success-light);
    border-radius: 0 var(--radius-full) var(--radius-full) 0;
  }

  .rollover-segment.using-rollover {
    background: var(--color-warning-light);
  }

  .rollover-segment.over-budget {
    background: var(--color-danger-light);
  }

  .segment-fill {
    height: 100%;
    transition: width var(--transition-base);
  }
</style>
